import { useTranslation } from 'react-i18next';
import { formatCents } from '@/shared/utils/format-cents';
import { formatLocalizedDate } from '@/shared/utils/date';
import {
  shouldNameTheStreak, type MeterView, type Offer,
} from '@/shared/utils/plan-tab-model';
import type { PlanWithFrequency } from '@features/onboarding/hooks/use-plans';
import type {
  QuotaCycleBlock, SeatUsageHistory,
} from '@services/requests/housien-services/AgenciesService.subscription';
import type { CatalogAddon } from '@services/requests/housien-services/AdministrationService';
import { OfferBlock } from './OfferBlock';
import { OfferExits, type OfferExit } from './OfferExits';
import { UpgradeInvite } from './UpgradeInvite';

interface Props {
  offer: Offer;
  meters: MeterView[];
  history: SeatUsageHistory | null;
  target: PlanWithFrequency | null;
  currentPriceCents: number | null;
  pack: CatalogAddon | null;
  onBuyPack: () => void;
  onCompare: () => void;
}

/**
 * Reads the history block that matches a meter.
 *
 * @param history - The history payload, when it arrived.
 * @param meter - The meter under pressure.
 * @returns The matching block, or null.
 */
function blockFor(
  history: SeatUsageHistory | null, meter: MeterView,
): QuotaCycleBlock | null {
  if (!history) return null;
  return meter.key === 'interaction' ? history.interaction : history.partnership_search;
}


/**
 * How many times over the next plan covers what is used today.
 *
 * Returns null rather than a number whenever the comparison would be
 * meaningless — no target limit, no current ceiling to compare against, or a
 * factor below two. "Multiplies by 1" is not an argument, and a factor derived
 * from a missing denominator is worse than saying nothing.
 *
 * @param target - The upgrade target.
 * @param meters - The current meters.
 * @returns The whole-number factor, or null.
 */
function upgradeFactor(
  target: PlanWithFrequency, meters: MeterView[],
): number | null {
  const targetInteractions = target.limits?.seat.interactions;
  const current = meters.find((meter) => meter.key === 'interaction')?.total;
  if (!targetInteractions || !current || current <= 0) return null;
  const factor = Math.round(targetInteractions / current);
  return factor >= 2 ? factor : null;
}

/**
 * Names what the next plan actually raises, in numbers.
 *
 * The offer has to be checkable against the meters directly above it, which
 * means concrete ceilings rather than the names of the things being raised.
 *
 * @param target - The upgrade target.
 * @param t - Translation function.
 * @returns A comma-separated list, possibly empty.
 */
function describeLimits(
  target: PlanWithFrequency,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  // Only the per-person allowances: the offer sits under the meters, which
  // count what one broker spent, and a shared pool would not be checkable
  // against them.
  const limits = target.limits?.seat ?? {};
  return [
    limits.interactions
      && `${limits.interactions} ${t('subscription.plan.usage.interaction').toLowerCase()}`,
    limits.partnership_searches
      && `${limits.partnership_searches} ${t('subscription.plan.usage.partnershipSearch').toLowerCase()}`,
  ].filter(Boolean).join(', ');
}

/**
 * The offer, conditioned entirely on the data.
 *
 * Nothing here decides whether to render — decideOffer did that, and this
 * only dresses the verdict. The alert's two exits keep the prototype's order:
 * solve the cycle first, change plan second.
 *
 * @param props - Component props.
 * @returns The offer block, or null.
 */
export function PlanOffer({
  offer, meters, history, target, currentPriceCents, pack, onBuyPack, onCompare,
}: Props) {
  const { t } = useTranslation('features/pricing');
  if (offer.kind === 'none') return null;

  if (offer.kind === 'invite') {
    if (!target) return null;
    const difference = formatCents(target.price * 100 - (currentPriceCents ?? 0));
    const factor = upgradeFactor(target, meters);
    const limits = describeLimits(target, t);
    return (
      <OfferBlock tone="invite" headline="" lines={[]}>
        <UpgradeInvite
          headline={factor
            ? t('subscription.plan.invite.title', { plan: target.title, factor })
            : t('subscription.plan.invite.titleNoFactor', { plan: target.title })}
          caption={t(currentPriceCents
            ? 'subscription.plan.invite.caption'
            : 'subscription.plan.invite.captionNoPrice', { limits, difference })}
          ctaLabel={t('subscription.plan.invite.cta', { plan: target.title })}
          onCta={onCompare}
          compareLabel={t('subscription.plan.invite.compare')}
          onCompare={onCompare}
        />
      </OfferBlock>
    );
  }

  const { meter, showPack, showPlan } = offer;
  const block = blockFor(history, meter);
  const when = formatLocalizedDate(block?.projected_exhaustion_date ?? undefined);
  const renewal = formatLocalizedDate(history?.cycle_end_date ?? undefined);

  const headline = when
    ? t(`subscription.plan.alert.nearEnd_${meter.key}`, { date: when })
    : t(`subscription.plan.alert.nearEndNoDate_${meter.key}`);

  const why = when && block?.days_before_renewal != null && history
    ? t('subscription.plan.alert.why', {
      days: block.days_before_renewal, elapsed: history.days_elapsed,
    })
    : t('subscription.plan.alert.whyUnknown', { used: meter.used, total: meter.total });

  const consequence = renewal
    ? t('subscription.plan.alert.consequence', { date: renewal })
    : t('subscription.plan.alert.consequenceNoDate');

  const exits: OfferExit[] = [];
  if (showPack && pack) {
    const units = pack.granted_quantity;
    exits.push({
      key: 'pack', emphasis: 'primary',
      title: t('subscription.plan.pack.title'),
      caption: t(renewal
        ? 'subscription.plan.pack.caption'
        : 'subscription.plan.pack.captionNoDate', { units, date: renewal }),
      ctaLabel: t('subscription.plan.pack.cta', {
        units, price: formatCents(pack.price_cents, { currency: pack.currency }),
      }),
      onCta: onBuyPack,
    });
  }
  if (showPlan && target) {
    const streak = block?.consecutive_over_quota_cycles;
    exits.push({
      key: 'plan', emphasis: 'secondary',
      title: t('subscription.plan.upgradeExit.title'),
      caption: [
        shouldNameTheStreak(streak)
          ? t('subscription.plan.upgradeExit.repeat', { count: streak })
          : '',
        t(currentPriceCents
          ? 'subscription.plan.upgradeExit.caption'
          : 'subscription.plan.upgradeExit.captionNoPrice', {
          plan: target.title,
          total: target.limits?.seat.interactions ?? target.users ?? '',
          difference: formatCents(target.price * 100 - (currentPriceCents ?? 0)),
        }),
      ].filter(Boolean).join(' '),
      ctaLabel: t('subscription.plan.upgradeExit.cta', { plan: target.title }),
      onCta: onCompare,
    });
  }

  return (
    <OfferBlock tone="alert" headline={headline} lines={[why, consequence]}>
      <OfferExits exits={exits} />
    </OfferBlock>
  );
}
